import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BPProfilesLocationsDataService } from '../../../../shared/bp-profiles/bp-profiles-locations-data.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { GetRowIdParams, GridOptions, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { StateService } from '@uirouter/angular';
import { IColDef } from '../../../../shared/report/report-grid.service';
import { BPProfilesService } from '../../bp-profiles.service';
import { GaAgGridService } from '../../../../shared/ag-grid-components/ga-ag-grid.service';
import { ICountryExtended, ILocationExtended, IObject } from '../../../../../type_definitions/datatypes';
import { FormattingService } from '../../../../shared/services/formatting.service';
import _ from 'underscore';
import { CellRendererComp } from '../../../../shared/ag-grid-components/ga-ag-grid.component';
import { IAgButtonDropdownRendererParams } from '../../../../shared/ag-grid-components/ag-button-dropdown/ag-button-dropdown-renderer.component';
import { AgLocationDetailComponent } from './ag-location-detail.component';
import { util } from '../../../../shared/util';
import { DialogBusyButtonFn, DialogSize, GaMessageService, IDialog, IDialogButton } from '../../../../shared/message/ga-message.service';
import { BPProfilesLocationsDialogComponent } from './bp-profiles-locations-dialog.component';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { BUTTON_CANCEL, BUTTON_DELETE } from '../../../../shared/message/dialogs/default-form-dialog.component';
import { GRID_BP_LOCATIONS } from '@app/shared/app-grids';

const gridHeightOffset:string = '45px;.ui-layout-north;.ui-layout-resizer-north;.fixed-to-top-container;.new-location-button;.filter-location-container';

enum GridColumn {
  OPTIONS = 'options',
  NAME = 'name',
  COUNTRY = 'country',
  ADDRESS = 'address',
  HEADQUARTER = 'headquarter'
}

export enum HeadquarterType {
  COMPANY = 'company',
  COUNTRY = 'country'
}

interface IFilterModel {
  showInactive?:boolean;
}

@Component({
  selector: 'bp-profiles-locations',
  template: `
    <div class="bp-profiles-locations">
      <div class="new-location-button margin-bottom-sm" *ngIf="hasWritePermission">
        <button class="btn btn-primary" translate [translateParams]="{ label: entityLabel }" (click)="onShowLocationDialog(true)">GA.BUTTONS.NEW_LABEL</button>
      </div>

      <div class="d-block">
        <formly [form]="form" [fields]="fields" [model]="filterModel"></formly>
      </div>

      <div class="d-block clear margin-top-sm">
        <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" fillHeight="${gridHeightOffset}" [height]="400"></ga-ag-grid>
      </div>
    </div>
  `,
  styleUrls: ['./bp-profiles-locations.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPProfilesLocationsComponent implements OnInit {
  public entityLabel:string;
  public gridOptions:GridOptions;
  public gridInit:boolean;
  public gridId:string = GRID_BP_LOCATIONS;
  public hasWritePermission:boolean;
  public filterModel:IFilterModel = {
    showInactive: false
  };
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[] = [];

  private locations:ILocationExtended[] = [];

  constructor(
    private formlyService:GaFormlyService,
    private messageService:GaMessageService,
    private formattingService:FormattingService,
    private bpProfilesService:BPProfilesService,
    private bpProfilesLocationsDataService:BPProfilesLocationsDataService,
    private translationService:TranslationService,
    private authService:AuthService,
    private stateService:StateService,
    private gaAgGridService:GaAgGridService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.entityLabel = this.translationService.instant('BPPROFILES.LOCATIONS.LOCATION.LABEL');
    this.hasWritePermission = this.authService.viewWrite(this.stateService.current.data?.viewCode);

    // create filter fields
    this.fields = this.createFields();

    // initialize ag-grid
    this.gridInitialize();
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aCheckbox('showInactive')
        .withoutWrapper()
        .onChange(() => this.refreshGridRowsData())
        .build()
    ];
  }

  private createColDefs():IColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumn.OPTIONS,
        headerName: '',
        sortable: false,
        pinned: 'left',
        width: 46,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        suppressColumnsToolPanel: true,
        cellClass: 'cell-actions text-center ag-button-dropdown-renderer',
        cellRenderer: CellRendererComp.BUTTON_DROPDOWN_RENDERER,
        cellRendererParams: <Partial<IAgButtonDropdownRendererParams>>{
          dropdownMenus: [
            {
              label: 'GA.BUTTONS.EDIT',
              onClick: this.onEditLocation,
              iconClass: 'fa-pencil'
            },
            {
              label: 'GA.BUTTONS.DELETE',
              onClick: this.onDeleteLocation,
              iconClass: 'fa-trash-o'
            },
            {
              label: 'BPPROFILES.LOCATIONS.SET_AS_COMPANY_HQ',
              onClick: (params:IAgButtonDropdownRendererParams):void => this.onSetLocationHeadquarter(params, HeadquarterType.COMPANY),
              iconClass: 'fa-globe',
              hideExpression: (params:IAgButtonDropdownRendererParams):boolean => !(<ILocationExtended>params.data).active || (<ILocationExtended>params.data).companyHQ
            },
            {
              label: 'BPPROFILES.LOCATIONS.SET_AS_COUNTRY_HQ',
              onClick: (params:IAgButtonDropdownRendererParams):void => this.onSetLocationHeadquarter(params, HeadquarterType.COUNTRY),
              iconClass: 'fa-flag',
              isHidden: !this.hasWritePermission,
              hideExpression: (params:IAgButtonDropdownRendererParams):boolean => !(<ILocationExtended>params.data).active || (<ILocationExtended>params.data).countryHQ
            }
          ]
        }
      },
      {
        field: GridColumn.COUNTRY,
        headerName: 'BPPROFILES.LOCATIONS.COUNTRY.LABEL',
        valueGetter: 'data.country && data.country.name',
        cellRenderer: 'agGroupCellRenderer',
        cellClassRules: this.getInactiveCellClass()
      },
      {
        field: GridColumn.NAME,
        headerName: 'BPPROFILES.CONTACTS.GRID.NAME',
        valueGetter: 'data.name',
        cellClassRules: this.getInactiveCellClass()
      },
      {
        field: GridColumn.ADDRESS,
        headerName: 'BPPROFILES.LOCATIONS.ADDRESS.LABEL',
        valueGetter: 'data.address',
        valueFormatter: (params:ValueFormatterParams):string => {
          const data:ILocationExtended = params.data;
          return this.formattingService.formatAddressLine(data?.address, data?.zipCode, data?.city);
        },
        cellClassRules: this.getInactiveCellClass()
      },
      {
        field: GridColumn.HEADQUARTER,
        headerName: 'BPPROFILES.LOCATIONS.HQ',
        cellClass: 'text-center',
        cellClassRules: this.getInactiveCellClass(),
        cellRenderer: (params:ICellRendererParams):string => {
          let value:string = '';
          const data:ILocationExtended = params.data;

          if (data.companyHQ) {
            value += `<i class="fa fa-globe margin-right-xs" title="${this.translationService.instant('BPPROFILES.LOCATIONS.COMPANYHQ.LABEL')}"></i>`;
          }

          if (data.countryHQ) {
            value += `<i class="fa fa-flag" title="${this.translationService.instant('BPPROFILES.LOCATIONS.COUNTRYHQ.LABEL')}"></i>`;
          }

          return value;
        }
      }
    ];

    _.forEach(colDefs, (colDef:IColDef) => {
      colDef.headerName = this.translationService.instant(colDef.headerName);

      if (colDef.field !== GridColumn.OPTIONS) {
        colDef.resizable = true;
      }
    });

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.createColDefs(),
      onGridReady: this.onGridReady,
      onRowDataUpdated: this.onRowDataUpdated,
      masterDetail: true,
      detailCellRenderer: 'agLocationDetail',
      getRowId: (row:GetRowIdParams<ILocationExtended>):string => row.data.id?.toString(),
      suppressMovableColumns: true,
      components: {
        agLocationDetail: AgLocationDetailComponent
      }
    };
    // mark grid as initialized
    this.gridInit = true;
  }

  private onGridReady = ():void => {
    if (!this.hasWritePermission) {
      // if user doesn't have 'write' permission then hide options column
      this.gaAgGridService.setColumnsVisible(this.gridId, [GridColumn.OPTIONS], false);
    }

    this.loadData();
  };

  private loadData():void {
    this.gaAgGridService.setGridLoading(this.gridId, true);
    this.bpProfilesLocationsDataService.getLocations(this.bpProfilesService.activeProfile?.id)
      .then((locations:ILocationExtended[]) => {
        this.locations = util.copyAndExtend(locations);
        this.refreshGridRowsData();
      })
      .finally(() => {
        this.gaAgGridService.setGridLoading(this.gridId, false);
      });
  }

  private getInactiveCellClass():IObject {
    return {
      'text-muted': (params:ICellRendererParams) => !params.data?.active
    };
  }

  private getDataIndex(id:number):number {
    return _.findIndex(this.locations, { id });
  }

  private onRowDataUpdated = ():void => {
    this.gaAgGridService.sizeColumnsToFit(this.gridId);
    this.gaAgGridService.autoSizeColumns(this.gridId, [GridColumn.ADDRESS, GridColumn.HEADQUARTER]);
  };

  private refreshGridRowsData():void {
    const data:ILocationExtended[] = _.chain(util.copyAndExtend(this.locations || []))
      .filter((location:ILocationExtended) => this.filterModel.showInactive ? true : location.active)
      .value()
      .sort((a:ILocationExtended, b:ILocationExtended):number => {
        const countryA:string = a.country? (<ICountryExtended>a.country).name : '';
        const countryB:string = b.country? (<ICountryExtended>b.country).name : '';
        const selectedProfileCountry:string = this.bpProfilesService.activeProfile?.country?.name;

        if (countryA === selectedProfileCountry && countryB !== selectedProfileCountry) {
          return -1;
        }

        if (countryB === selectedProfileCountry && countryA !== selectedProfileCountry) {
          return 1;
        }

        if (countryA < countryB) {
          return -1;
        }

        if (countryA > countryB) {
          return 1;
        }

        return a.name < b.name? -1 : (a.name > b.name)? 1 : 0;
      });

    this.gaAgGridService.setRowData(this.gridId, data, true);
  }

  public onShowLocationDialog(isAdding:boolean, location?:ILocationExtended):void {
    const model:ILocationExtended = util.copyAndExtend(location || {});

    if (util.isNullOrUndefinedOrEmpty(model.country)) {
      // if country is not exist should always preselect based on bp profile country
      model.country = util.copyAndExtend(this.bpProfilesService.activeProfile?.country || {});
    }

    if (isAdding) {
      // when adding new location, default location is active
      model.active = true;
    }

    const dialog:IDialog = this.messageService.showDialog({
      component: BPProfilesLocationsDialogComponent,
      wrapperComponent: false,
      size: DialogSize.MD,
      inputs: {
        adding: isAdding,
        entity: 'BPPROFILES.LOCATIONS.LOCATION.LABEL',
        model
      },
      outputs: {
        onSave: (model:ILocationExtended):void => {
          let promise:Promise<ILocationExtended>;
          const profileId:number = this.bpProfilesService.activeProfile?.id;

          if (isAdding) {
            promise = this.bpProfilesLocationsDataService.addNewLocation(profileId, model);
          } else {
            promise = this.bpProfilesLocationsDataService.updateLocation(profileId, model);
          }

          dialog.content.inputs.saving = true;
          promise
            .then((location:ILocationExtended) => {
              if (isAdding) {
                this.locations.push(location);
              } else {
                this.locations[this.getDataIndex(location.id)] = location;
              }
              // refresh grid rows data
              this.refreshGridRowsData();
              this.gaAgGridService.highlightGridRow(this.gridId, location.id);
              // close dialog
              dialog.hide();
            })
            .finally(() => {
              dialog.content.inputs.saving = false;
            });
        },
        onCancel: () => dialog.hide()
      }
    });
  }

  private onEditLocation = (params:IAgButtonDropdownRendererParams):void => {
    this.onShowLocationDialog(false, params.data);
  };

  private onDeleteLocation = (params:IAgButtonDropdownRendererParams):void => {
    const dialogButtons:IDialogButton[] = [
      {
        id: BUTTON_DELETE,
        label: 'GA.BUTTONS.DELETE',
        onClick: (updateBusyButtonStatus:DialogBusyButtonFn) => {
          updateBusyButtonStatus(true);
          this.bpProfilesLocationsDataService.deleteLocation(this.bpProfilesService.activeProfile?.id, params.data)
            .then(() => {
              // remove it from the list
              this.locations.splice(this.getDataIndex(params.data?.id), 1);
              this.refreshGridRowsData();
              // close dialog
              dialog.hide();
            })
            .finally(() => {
              updateBusyButtonStatus(false);
            });
        },
        className: 'btn-danger'
      },
      { id: BUTTON_CANCEL, label: this.translationService.instant('GA.BUTTONS.DONT_LABEL', { label: 'GA.BUTTONS.DELETE' }), onClick: () => dialog.hide(), className: 'btn-success' }
    ];

    const dialog:IDialog = this.messageService.showMessageDialog(
      'GA.GENERAL.CONFIRM_DELETE',
      this.translationService.instant('BPPROFILES.LOCATIONS.CONFIRM_DELETE_LOCATION', { locationName: params.data?.name }),
      dialogButtons,
      undefined,
      undefined,
      DialogSize.MD
    );
  };

  private onSetLocationHeadquarter = (params:IAgButtonDropdownRendererParams, type?:HeadquarterType):void => {
    this.bpProfilesLocationsDataService.setLocationHeadquarter(this.bpProfilesService.activeProfile?.id, params.data, type)
      .then(() => {
        const dataIndex:number = this.getDataIndex(params.data?.id);

        if (type === HeadquarterType.COMPANY) {
          // set existing locations' company HQ to false
          // because company HQ can only be in 1 location
          _.forEach(this.locations, (location:ILocationExtended) => {
            location.companyHQ = false;
          });
          // set selected location as company headquarter
          this.locations[dataIndex].companyHQ = true;
        } else {
          // set existing locations' country HQ to false
          // because country HQ can only be one in 1 country
          _.forEach(this.locations, (location:ILocationExtended) => {
            if ((<ICountryExtended>location.country).id === params.data.country?.id) {
              location.countryHQ = false;
            }
          });
          // set selected location as country HQ
          this.locations[dataIndex].countryHQ = true;
        }

        // update ag grid data
        this.refreshGridRowsData();
        this.gaAgGridService.highlightGridRow(this.gridId, params.data?.id);
      });
  };
}